<script setup lang="ts">
import NumberFlow from "@number-flow/vue";
import { twMerge } from "tailwind-merge";

import { useDiscount } from "@/composables/api/useDiscount";
import { useUser } from "@/composables/api/useUser";

import EuroBadgeIcon from "@/components/icons/EuroBadgeIcon.vue";
import Button from "@/components/ui/Button.vue";
import Spinner from "@/components/ui/Spinner.vue";

const { data: user } = useUser();

const { data: discount, isLoading } = useDiscount();

function openAutologinLink() {
  if (discount.value?.autologin_link) {
    window.open(discount.value.autologin_link, "_blank");
  }
}
</script>

<template>
  <section class="flex flex-col items-center gap-10 py-10">
    <Spinner v-if="isLoading || !discount" class="size-24" />

    <div
      v-else
      class="flex w-full max-w-xs flex-col items-center gap-4 rounded-[20px] bg-white p-5 shadow-xl lg:max-w-lg lg:gap-8 lg:p-10"
    >
      <h2
        :class="
          twMerge(
            'text-galbani text-center text-xl font-semibold lg:text-2xl',
            user?.current_mechanic === 'lactel' &&
              'text-lactel-blue font-mitr text-2xl font-bold lg:text-3xl',
          )
        "
        v-html="discount.count === 0 ? $t('vouchers.emptyTitle') : $t('vouchers.title')"
      />

      <p
        class="text-primary text-center font-medium lg:text-xl [&>span]:font-bold"
        v-html="
          discount.count === 0
            ? $t('vouchers.emptyDescription')
            : $t('vouchers.description', { n: discount.count })
        "
      />

      <div class="relative flex w-[186px] flex-col overflow-hidden lg:w-[220px]">
        <svg
          width="222"
          height="21"
          viewBox="0 0 222 21"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            id="Vector"
            d="M0.579346 20.7852L0.580093 6.98566L16.2196 0.000108719L32.1287 6.98566L47.7682 0.000108719L63.6773 6.98566L79.3168 0.000108719L95.226 6.98566L110.865 0.000108719L126.775 6.98566L142.414 0.000108719L158.323 6.98566L173.963 0.000108719L189.872 6.98566L205.511 0.000108719L221.42 6.98566L221.42 20.7852H0.579346Z"
            fill="#FE5700"
          />
        </svg>

        <span
          class="bg-orange -my-0.5 flex w-full items-center justify-center gap-2 py-4 text-[39px] font-semibold text-white lg:text-[50px] [&>svg]:size-10 lg:[&>svg]:size-12"
          aria-label="{{ parseFloat(discount.amount) }} €"
        >
          <NumberFlow
            :value="parseFloat(discount.amount)"
            :format="{
              style: 'decimal',
              useGrouping: true,
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            }"
            aria-hidden="true"
          />

          <EuroBadgeIcon aria-hidden="true" />
        </span>

        <svg
          width="222"
          height="21"
          viewBox="0 0 222 21"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            id="Vector"
            d="M0.579346 0.214844L0.580093 14.0143L16.2196 20.9999L32.1287 14.0143L47.7682 20.9999L63.6773 14.0143L79.3168 20.9999L95.226 14.0143L110.865 20.9999L126.775 14.0143L142.414 20.9999L158.323 14.0143L173.963 20.9999L189.872 14.0143L205.511 20.9999L221.42 14.0143L221.42 0.214844H0.579346Z"
            fill="#FE5700"
          />
        </svg>
      </div>

      <Button
        v-if="discount.count > 0 && discount.autologin_link"
        variant="secondary"
        @click="openAutologinLink"
      >
        {{ $t("vouchers.button") }}
      </Button>
    </div>
  </section>
</template>
